import { useCallback } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { EntityType } from '@/modules/entities/types/EntityType';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import {
  AugmentedCreateDispositionScenarioInput,
  AugmentedUpdateDispositionScenarioInput,
  AugmentedUpdateEntityInput,
} from '@/types/schema';
import { UnreachableError } from '@/utils/errors';

import { useHideNoDispositionsBadgeMutation } from '../graphql/HideNoDispositionsBadge.generated';

interface DispositionScenario {
  id: string;
  reviewedAt?: Date | null;
}

export interface UseHideNoDispositionsBadgeParams {
  entityId: string;
  entitySubtypeId: string;
  entityType: EntityType;
  /** Existing disposition scenarios to mark as reviewed */
  dispositionScenarios?: DispositionScenario[] | null;
}

/**
 * Hook to hide the "No dispositions" badge by marking disposition scenarios as reviewed.
 *
 * If the entity has existing unreviewed disposition scenarios (e.g., AI-generated),
 * this hook updates those scenarios by setting reviewedAt to the current time.
 *
 * If the entity has no disposition scenarios at all, it creates new empty scenarios
 * with reviewedAt set (one for each primary client in the household).
 */
export function useHideNoDispositionsBadge({
  entityId,
  entitySubtypeId,
  entityType,
  dispositionScenarios,
}: UseHideNoDispositionsBadgeParams) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { householdId, primaryClients } = useHouseholdDetailsContext();

  const [hideNoDispositionsBadgeMutation, { loading }] =
    useHideNoDispositionsBadgeMutation({
      onError: (error) => {
        showFeedback('Unable to hide badge. Please try again.');
        reportError('Error hiding no dispositions badge', error);
      },
    });

  const hideNoDispositionsBadge = useCallback(() => {
    // Check if there are existing unreviewed scenarios to update
    const unreviewedScenarios =
      dispositionScenarios?.filter((scenario) => !scenario.reviewedAt) ?? [];
    const hasUnreviewedScenarios = unreviewedScenarios.length > 0;

    if (hasUnreviewedScenarios) {
      // Update existing unreviewed scenarios by setting reviewedAt
      const updateScenarioInputs: AugmentedUpdateDispositionScenarioInput[] =
        unreviewedScenarios.map((scenario) => ({
          id: scenario.id,
          update: {
            reviewedAt: new Date(),
          },
        }));

      const updateSubtypeInput = {
        id: entitySubtypeId,
        update: {},
        updateDispositionScenarios: updateScenarioInputs,
      };

      const input = buildEntityInput(
        entityId,
        entityType,
        updateSubtypeInput,
        'update'
      );

      return hideNoDispositionsBadgeMutation({
        variables: { input },
      });
    }

    // No existing scenarios - create new empty reviewed scenarios
    if (!householdId || !primaryClients || primaryClients.length === 0) {
      reportError(
        'Cannot hide badge: missing household ID or primary clients',
        new Error('Missing required data for hiding badge')
      );
      return;
    }

    // Create a disposition scenario for each primary client
    const createScenarioInputs: AugmentedCreateDispositionScenarioInput[] =
      primaryClients.map((client) => ({
        create: {
          householdID: householdId,
          firstGrantorDeathID: client.id,
          reviewedAt: new Date(),
        },
      }));

    const updateSubtypeInput = {
      id: entitySubtypeId,
      update: {},
      withDispositionScenarios: createScenarioInputs,
    };

    const input = buildEntityInput(
      entityId,
      entityType,
      updateSubtypeInput,
      'create'
    );

    return hideNoDispositionsBadgeMutation({
      variables: { input },
    });
  }, [
    entityId,
    entitySubtypeId,
    entityType,
    dispositionScenarios,
    householdId,
    primaryClients,
    hideNoDispositionsBadgeMutation,
    reportError,
  ]);

  return {
    hideNoDispositionsBadge,
    loading,
  };
}

type CreateDispositionScenariosInput = {
  id: string;
  update: Record<string, never>;
  withDispositionScenarios: AugmentedCreateDispositionScenarioInput[];
};

type UpdateDispositionScenariosInput = {
  id: string;
  update: Record<string, never>;
  updateDispositionScenarios: AugmentedUpdateDispositionScenarioInput[];
};

/**
 * Builds the entity-type-specific input for the updateEntity mutation.
 * Follows the same pattern as getEntityDispositiveProvisionsInput in DispositiveProvisionsForm.utils.ts
 */
function buildEntityInput(
  entityId: string,
  entityType: EntityType,
  updateSubtypeInput:
    | CreateDispositionScenariosInput
    | UpdateDispositionScenariosInput,
  _mode: 'create' | 'update'
): AugmentedUpdateEntityInput {
  const sharedProperties = {
    id: entityId,
    update: {},
  };

  switch (entityType) {
    case 'revocable-trust':
      return {
        ...sharedProperties,
        updateRevocableTrust: updateSubtypeInput,
      };
    case 'irrevocable-trust':
      return {
        ...sharedProperties,
        updateIrrevocableTrust: updateSubtypeInput,
      };
    case 'slat':
      return {
        ...sharedProperties,
        updateSlatTrust: updateSubtypeInput,
      };
    case 'grat':
      return {
        ...sharedProperties,
        updateGratTrust: updateSubtypeInput,
      };
    case 'qprt':
      return {
        ...sharedProperties,
        updateQprtTrust: updateSubtypeInput,
      };
    case 'ilit':
      return {
        ...sharedProperties,
        updateIlitTrust: updateSubtypeInput,
      };
    case 'individual-account':
      return {
        ...sharedProperties,
        updateIndividualPersonalAccount: updateSubtypeInput,
      };
    case 'joint-account':
      return {
        ...sharedProperties,
        updateJointPersonalAccount: updateSubtypeInput,
      };
    case 'custodial-account':
      return {
        ...sharedProperties,
        updateCustodialPersonalAccount: updateSubtypeInput,
      };
    case 'retirement-account':
      return {
        ...sharedProperties,
        updateRetirementPersonalAccount: updateSubtypeInput,
      };
    case 'qualified-tuition-account':
      return {
        ...sharedProperties,
        updateQualifiedTuitionPersonalAccount: updateSubtypeInput,
      };
    case 'clt':
      return {
        ...sharedProperties,
        updateCltTrust: updateSubtypeInput,
      };
    case 'crt':
      return {
        ...sharedProperties,
        updateCrtTrust: updateSubtypeInput,
      };
    case 'daf':
      return {
        ...sharedProperties,
        updateDonorAdvisedFund: updateSubtypeInput,
      };
    case 'private-foundation':
      return {
        ...sharedProperties,
        updatePrivateFoundation: updateSubtypeInput,
      };
    case 'insurance-account':
      return {
        ...sharedProperties,
        updateInsurancePersonalAccount: updateSubtypeInput,
      };
    // Business entities don't support dispositive provisions
    case 'sole-proprietorship':
    case 'c-corp':
    case 's-corp':
    case 'llc':
    case 'lp':
    case 'gp':
      throw new Error(
        'Business entities do not support dispositive provisions'
      );
    default:
      throw new UnreachableError({
        case: entityType,
        message: `Unrecognized entity type ${entityType}`,
      });
  }
}
